import { useState, useCallback, useRef } from "react";
import { debounce } from "@/utils/debounce";
import { executeGraphQLMutation } from "@/utils/helper/graphql-helper";
import { listProductInfos, listQuickReorders } from "@/graphql/queries";

interface UseFilteredQuickReordersSearchOptions {
  debounceMs?: number;
}

interface SearchFilters {
  search: string;
  brandId?: string;
  categoryId?: string;
  userId: string;
}

export function useFilteredQuickReordersSearch({
  debounceMs = 300,
}: UseFilteredQuickReordersSearchOptions = {}) {
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const debouncedSearchRef = useRef(
    debounce(async (filters: SearchFilters) => {
      try {
        console.log("filters: ", filters);
        const graphqlFilter: any = {
          user_id: { eq: filters.userId },
        };
        if (filters.brandId && filters.brandId !== "all") {
          graphqlFilter.brand_id = { contains: filters.brandId };
        }
        if (filters.categoryId && filters.categoryId !== "all") {
          graphqlFilter.category_id = { contains: filters.categoryId };
        }
        const response = await executeGraphQLMutation({
          query: listProductInfos,
          variables: {
            filter: { name: { contains: filters.search } },
            limit: 100,
          },
          showToast: false,
        });
        const productIds = response.items
          .map((item: any) => item.id)
          .filter(Boolean);
        console.log("Product Ids:", productIds);

        //  or: userIds.map((userId) => ({ user_id: { eq: userId } })),
        const res = await executeGraphQLMutation({
          query: listQuickReorders,
          variables: {
            filter: {
              and: [
                graphqlFilter,
                {
                  or: productIds.map((productId: any) => ({
                    product_id: { eq: productId },
                  })),
                },
              ],
            },
            limit: 100,
          },
          showToast: false,
        });
        console.log("Results: ", res);
        setResults(res?.items || []);
      } catch (err: any) {
        setError(err.message || "Error searching quick reorders");
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, debounceMs)
  );

  const searchQuickReorders = useCallback((filters: SearchFilters) => {
    if (
      filters.search.trim() ||
      (filters.brandId && filters.brandId !== "all") ||
      (filters.categoryId && filters.categoryId !== "all")
    ) {
      setLoading(true);
      setError(null);
      debouncedSearchRef.current(filters);
    } else {
      setResults([]);
      setLoading(false);
      setError(null);
    }
  }, []);

  const clearResults = useCallback(() => {
    setResults([]);
    setError(null);
    setLoading(false);
  }, []);

  return {
    results,
    loading,
    error,
    searchQuickReorders,
    clearResults,
  };
}
